import { Money } from '@dintero/money';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { default as Alert } from 'antd/es/alert';
import { useEffect, useMemo } from 'react';
import { getCryptoDecimals, isCryptoCode } from 'shared/src/cryptoUtils';

import { formatDate, formatHours } from 'utils/dayjs';
import { formatRenderedCurrency } from 'utils/formatters/formatCurrency';
import { hasValue } from 'utils/hasValue';

import { ErrorBoundary } from '@templates/ErrorBoundary';

import { useFxRate } from 'hooks/useFxRate';

import { Spinner } from '@atoms/Spinner/Spinner';

type Props = {
  visible?: boolean;
  payCurrencyCode?: string;
  buyCurrencyCode?: string;
  amount?: string;
  setIsFXConfigured?: (ok: boolean) => void;
  className?: string;
  remountKey?: string;
};

export function FXDetails({
  visible = true,
  payCurrencyCode,
  buyCurrencyCode,
  amount,
  setIsFXConfigured,
  className = 'm-2',
  remountKey
}: Props) {
  if (!visible) return null;

  return (
    <ErrorBoundary
      key={remountKey}
      fallback={
        <Alert
          message="Exchange rate is not available"
          type="error"
          className="mt-3"
        />
      }
      onErrorCatch={() => setIsFXConfigured?.(false)}
    >
      <FXDetailsBody
        payCurrencyCode={payCurrencyCode}
        buyCurrencyCode={buyCurrencyCode}
        amount={amount}
        setIsFXConfigured={setIsFXConfigured}
        className={className}
      />
    </ErrorBoundary>
  );
}

function FXDetailsBody({
  payCurrencyCode,
  buyCurrencyCode,
  amount,
  setIsFXConfigured,
  className
}: {
  payCurrencyCode?: string;
  buyCurrencyCode?: string;
  amount?: string;
  setIsFXConfigured?: (ok: boolean) => void;
  className?: string;
}) {
  const { fx, error, success } = useFxRate({
    payCurrencyCode,
    buyCurrencyCode
  });

  useEffect(() => {
    setIsFXConfigured?.(success);
  }, [setIsFXConfigured, success]);

  const suggestedTotal = useMemo(() => {
    if (
      !hasValue(amount) ||
      !hasValue(fx?.reciprocalExchangeRate) ||
      !hasValue(buyCurrencyCode)
    ) {
      return '...';
    }

    const decimals = isCryptoCode(buyCurrencyCode)
      ? getCryptoDecimals(buyCurrencyCode)
      : 2;

    try {
      const calculated = Money.fromPrice(
        fx!.reciprocalExchangeRate,
        buyCurrencyCode!
      )
        .multiply(Number(amount))
        .merge({ decimals })
        .toNumber();

      return formatRenderedCurrency(calculated, buyCurrencyCode);
    } catch {
      // toNumber throws once the converted amount exceeds what a double holds
      // exactly. This runs during render, so letting it escape takes the dialog
      // down; such an amount is over the withdrawal limit anyway.
      return '...';
    }
  }, [amount, buyCurrencyCode, fx]);

  if (error) {
    setIsFXConfigured?.(false);
    return (
      <Alert
        className="mt-3 w-full border-none text-feedback-error"
        message="Exchange rate is not available."
        type="error"
        icon={<ExclamationCircleIcon className="size-5" />}
        showIcon
      />
    );
  }

  if (!fx) {
    return <Spinner />;
  }

  return (
    <div className={className}>
      <p className="text-xs font-medium text-gray-500">
        {formatDate(fx.utcTime)} {formatHours(fx.utcTime)}
      </p>
      <p className="text-sm font-medium text-gray-500">
        For your {amount} {fx.payCurrency}, the current exchange rate will
        amount to approximately {suggestedTotal} {fx.buyCurrency}.
      </p>
    </div>
  );
}
